import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminSidebar from "./AdminSidebar";
import { getStudent, updateStudent, getAdminPayments, getResults } from "../api/adminclient";

const TABS = [
  { key: "profile", label: "Profile", icon: "bi-person-circle" },
  { key: "courses", label: "Courses", icon: "bi-mortarboard" },
  { key: "registrations", label: "Registrations", icon: "bi-clipboard-check" },
  { key: "payments", label: "Payments", icon: "bi-credit-card" },
  { key: "results", label: "Results", icon: "bi-graph-up" },
];

export default function AdminStudentDetail() {
  const { studentId } = useParams();
  const navigate = useNavigate();

  const [student, setStudent] = useState(null);
  const [payments, setPayments] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("profile");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([getStudent(studentId), getAdminPayments(), getResults()])
      .then(([s, p, r]) => {
        const studentData = s?.data || {};
        // Support the name fields used by different Django serializers.
        const resolvedName =
          studentData.fullName ||
          studentData.full_name ||
          studentData.name ||
          studentData.username ||
          "";

        setStudent({
          ...studentData,
          fullName: resolvedName,
          email: studentData.email || "",
          mobile: studentData.mobile || "",
          gender: studentData.gender || "",
          registeredAt: studentData.registeredAt || studentData.registered_at || "",
          courses: Array.isArray(studentData.courses)
            ? studentData.courses
            : Array.isArray(studentData.courseNames)
              ? studentData.courseNames
              : [],
        });

        const paymentsData = Array.isArray(p?.data) ? p.data : [];
        const resultsData = Array.isArray(r?.data) ? r.data : [];
        setPayments(paymentsData.filter((x) =>
          String(x.studentId ?? x.student_id ?? x.userId ?? x.user_id) === String(studentId)
        ));
        setResults(resultsData.filter((x) =>
          String(x.userId ?? x.user_id ?? x.studentId ?? x.student_id) === String(studentId)
        ));
      })
      .catch((error) => {
        console.error("Could not load student details:", error);
        setStudent(null);
      })
      .finally(() => setLoading(false));
  }, [studentId]);

  const toggleStatus = async () => {
    if (!student) return;
    const nextStatus = (student.status || "Active") === "Active" ? "Inactive" : "Active";
    setSaving(true);
    try {
      const { data } = await updateStudent(student.id, { status: nextStatus });
      setStudent(data);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-page-full d-flex">
        <AdminSidebar />
        <div className="admin-content"><p className="text-muted">Loading student...</p></div>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="admin-page-full d-flex">
        <AdminSidebar />
        <div className="admin-content">
          <div className="alert alert-warning">Student not found.</div>
        </div>
      </div>
    );
  }

  const courses = Array.isArray(student.courses) ? student.courses : student.course ? [student.course] : [];
  const status = student.status || "Active";

  return (
    <div className="admin-page-full d-flex">
      <AdminSidebar />
      <div className="admin-content">
        <button className="btn btn-link btn-sm text-secondary text-decoration-none ps-0 mb-2" onClick={() => navigate("/admin/students")}>
          <i className="bi bi-arrow-left me-1" /> Back to Students
        </button>

        <div className="row g-3">
          <div className="col-12 col-lg-3">
            <div className="admin-card p-3 p-md-4 text-center">
              <div
                className="rounded-circle bg-primary bg-opacity-10 d-inline-flex align-items-center justify-content-center mb-3"
                style={{ width: 80, height: 80 }}
              >
                <i className="bi bi-person-fill fs-2 text-primary" />
              </div>
              <h3 className="h6 fw-bold mb-0">{student.fullName || student.full_name || student.name || student.username || "Name not available"}</h3>
              <p className="text-muted small mb-2">{student.email}</p>
              <span className={`admin-status-pill ${String(status).toLowerCase()}`}>{status}</span>

              <nav className="d-flex flex-column gap-1 mt-4 text-start">
                {TABS.map((t) => (
                  <button
                    key={t.key}
                    className={`btn btn-sm text-start ${tab === t.key ? "btn-primary" : "btn-light"}`}
                    onClick={() => setTab(t.key)}
                  >
                    <i className={`bi ${t.icon} me-2`} /> {t.label}
                  </button>
                ))}
              </nav>

              <button
                className={`btn btn-sm w-100 mt-3 ${status === "Active" ? "btn-outline-danger" : "btn-outline-success"}`}
                onClick={toggleStatus}
                disabled={saving}
              >
                {status === "Active" ? "Deactivate" : "Activate"} Student
              </button>
            </div>
          </div>

          <div className="col-12 col-lg-9">
            <div className="admin-card p-3 p-md-4">
              {tab === "profile" && (
                <>
                  <h3 className="h6 fw-bold mb-3">Profile</h3>
                  <table className="table table-borderless mb-0">
                    <tbody>
                      <tr><td className="text-muted small">Full Name</td><td>: {student.fullName || student.full_name || student.name || student.username || "Name not available"}</td></tr>
                      <tr><td className="text-muted small">Email</td><td>: {student.email}</td></tr>
                      <tr><td className="text-muted small">Mobile</td><td>: {student.mobile || "—"}</td></tr>
                      <tr><td className="text-muted small">Gender</td><td>: {student.gender || "—"}</td></tr>
                      <tr><td className="text-muted small">Registered</td><td>: {student.registeredAt || "—"}</td></tr>
                    </tbody>
                  </table>
                </>
              )}

              {tab === "courses" && (
                <>
                  <h3 className="h6 fw-bold mb-3">Courses</h3>
                  {courses.length === 0 && <p className="text-muted small mb-0">No courses selected.</p>}
                  <div className="d-flex flex-wrap gap-2">
                    {courses.map((c) => (
                      <span className="badge oes-course-badge" key={c}>{c}</span>
                    ))}
                  </div>
                </>
              )}

              {tab === "registrations" && (
                <>
                  <h3 className="h6 fw-bold mb-3">Registrations</h3>
                  <p className="text-muted small mb-0">
                    See the Registrations page for this student's exam bookings and payment/schedule status.
                  </p>
                </>
              )}

              {tab === "payments" && (
                <>
                  <h3 className="h6 fw-bold mb-3">Payments</h3>
                  {payments.length === 0 && <p className="text-muted small mb-0">No payments recorded.</p>}
                  {payments.length > 0 && (
                    <table className="table admin-table align-middle mb-0">
                      <thead>
                        <tr><th>Transaction ID</th><th>Amount</th><th>Status</th><th>Date</th></tr>
                      </thead>
                      <tbody>
                        {payments.map((p) => (
                          <tr key={p.id}>
                            <td>{p.transactionId}</td>
                            <td>₹{Number(p.amount).toLocaleString("en-IN")}</td>
                            <td><span className={`admin-status-pill ${p.status === "SUCCESS" ? "active" : p.status === "PENDING" ? "pending" : "inactive"}`}>{p.status}</span></td>
                            <td className="text-muted small">{p.date}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              )}

              {tab === "results" && (
                <>
                  <h3 className="h6 fw-bold mb-3">Results</h3>
                  {results.length === 0 && <p className="text-muted small mb-0">No results recorded.</p>}
                  {results.length > 0 && (
                    <table className="table admin-table align-middle mb-0">
                      <thead>
                        <tr><th>Course</th><th>Score</th><th>Result</th></tr>
                      </thead>
                      <tbody>
                        {results.map((r) => (
                          <tr key={r.id}>
                            <td>{r.courseName}</td>
                            <td>{r.score}/{r.total}</td>
                            <td>
                              <span className={`admin-status-pill ${r.result?.startsWith("PASS") ? "active" : "inactive"}`}>
                                {r.result}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}